import React, { Component } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell, Area, ComposedChart } from 'recharts';
import { Download, AlertTriangle, MapPin, TrendingUp } from 'lucide-react';
// --- Mock Data ---
const lineSizeData = [{
  month: 'Jan',
  average: 450,
  maximum: 2500,
  threshold: 3000
}, {
  month: 'Feb',
  average: 480,
  maximum: 2800,
  threshold: 3000
}, {
  month: 'Mar',
  average: 520,
  maximum: 2600,
  threshold: 3000
}, {
  month: 'Apr',
  average: 490,
  maximum: 2900,
  threshold: 3000
}, {
  month: 'May',
  average: 550,
  maximum: 3200,
  threshold: 3000
}, {
  month: 'Jun',
  average: 580,
  maximum: 2700,
  threshold: 3000
}, {
  month: 'Jul',
  average: 600,
  maximum: 2850,
  threshold: 3000
}, {
  month: 'Aug',
  average: 620,
  maximum: 3100,
  threshold: 3000
}, {
  month: 'Sep',
  average: 590,
  maximum: 2950,
  threshold: 3000
}, {
  month: 'Oct',
  average: 610,
  maximum: 3050,
  threshold: 3000
}, {
  month: 'Nov',
  average: 630,
  maximum: 2800,
  threshold: 3000
}, {
  month: 'Dec',
  average: 650,
  maximum: 2900,
  threshold: 3000
}];
const policyLimitsData = [{
  range: '£0-250K',
  count: 145,
  percentage: 35
}, {
  range: '£250-500K',
  count: 120,
  percentage: 29
}, {
  range: '£500K-1M',
  count: 85,
  percentage: 21
}, {
  range: '£1-2M',
  count: 42,
  percentage: 10
}, {
  range: '£2-5M',
  count: 18,
  percentage: 4
}, {
  range: '£5M+',
  count: 5,
  percentage: 1
}];
const geographicExposureData = [{
  region: 'London',
  exposure: 4500,
  percentage: 28,
  status: 'high'
}, {
  region: 'South East',
  exposure: 3200,
  percentage: 20,
  status: 'medium'
}, {
  region: 'North West',
  exposure: 2800,
  percentage: 17,
  status: 'medium'
}, {
  region: 'Scotland',
  exposure: 1900,
  percentage: 12,
  status: 'low'
}, {
  region: 'Midlands',
  exposure: 1600,
  percentage: 10,
  status: 'low'
}, {
  region: 'South West',
  exposure: 1200,
  percentage: 7,
  status: 'low'
}, {
  region: 'Wales',
  exposure: 800,
  percentage: 5,
  status: 'low'
}, {
  region: 'Northern Ireland',
  exposure: 200,
  percentage: 1,
  status: 'low'
}];
const occupancyExposureData = [{
  occupancy: 'Office Buildings',
  exposure: 3800,
  limit: 4000,
  percentage: 95
}, {
  occupancy: 'Retail Premises',
  exposure: 2900,
  limit: 3500,
  percentage: 83
}, {
  occupancy: 'Manufacturing',
  exposure: 2400,
  limit: 3000,
  percentage: 80
}, {
  occupancy: 'Warehousing',
  exposure: 1800,
  limit: 2500,
  percentage: 72
}, {
  occupancy: 'Hospitality',
  exposure: 1500,
  limit: 2000,
  percentage: 75
}, {
  occupancy: 'Healthcare',
  exposure: 1200,
  limit: 2000,
  percentage: 60
}, {
  occupancy: 'Education',
  exposure: 900,
  limit: 1500,
  percentage: 60
}, {
  occupancy: 'Residential',
  exposure: 700,
  limit: 1500,
  percentage: 47
}];
// --- Colors ---
const COLORS = {
  average: '#3B82F6',
  maximum: '#EF4444',
  threshold: '#F59E0B',
  low: '#DBEAFE',
  medium: '#60A5FA',
  high: '#1E40AF',
  breach: '#DC2626' // Dark red
};
// --- Helper Functions ---
const getIntensityColor = (percentage: number) => {
  if (percentage < 50) return COLORS.low;
  if (percentage < 75) return COLORS.medium;
  if (percentage < 90) return COLORS.high;
  return COLORS.breach;
};
const getStatusColor = (status: string) => {
  switch (status) {
    case 'low':
      return COLORS.low;
    case 'medium':
      return COLORS.medium;
    case 'high':
      return COLORS.high;
    default:
      return COLORS.low;
  }
};
// --- Components ---
const CustomTooltip = ({
  active,
  payload,
  label,
  unit = '£K'
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-md text-sm" data-id="element-1575">
        <p className="font-semibold text-gray-900 mb-2" data-id="element-1576">{label}</p>
        {payload.map((entry: any, index: number) => <div key={index} className="flex items-center gap-2 mb-1 last:mb-0" data-id="element-1577">
            <div className="w-3 h-3 rounded-full" style={{
          backgroundColor: entry.color || entry.fill
        }} data-id="element-1578" />
            <span className="text-gray-600" data-id="element-1579">{entry.name}:</span>
            <span className="font-medium text-gray-900" data-id="element-1580">
              {unit === '£K' ? '£' : ''}
              {entry.value.toLocaleString()}
              {unit === '£K' ? 'K' : unit === '%' ? '%' : ''}
            </span>
          </div>)}
      </div>;
  }
  return null;
};
const GeographicHeatmap = () => {
  return <div className="space-y-3" data-id="element-1581">
      {geographicExposureData.map((region, index) => {
      const intensity = region.percentage / 30; // Normalize to 0-1 scale (max 30%)
      return <div key={index} className="flex items-center gap-3" data-id="element-1582">
            <div className="w-32 text-sm font-medium text-gray-700" data-id="element-1583">
              {region.region}
            </div>
            <div className="flex-1" data-id="element-1584">
              <div className="relative h-8 bg-gray-100 rounded overflow-hidden" data-id="element-1585">
                <div className="absolute inset-y-0 left-0 rounded transition-all duration-500" style={{
              width: `${region.percentage * 3.33}%`,
              backgroundColor: getStatusColor(region.status),
              opacity: 0.3 + intensity * 0.7 // Gradient from 30% to 100% opacity
            }} data-id="element-1586" />
                <div className="absolute inset-0 flex items-center justify-between px-3" data-id="element-1587">
                  <span className="text-xs font-medium text-gray-700" data-id="element-1588">
                    £{(region.exposure / 1000).toFixed(1)}M
                  </span>
                  <span className="text-xs text-gray-500" data-id="element-1589">
                    {region.percentage}%
                  </span>
                </div>
              </div>
            </div>
            {region.status === 'high' && <AlertTriangle className="text-amber-500 flex-shrink-0" size={16} data-id="element-1590" />}
          </div>;
    })}
      <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500" data-id="element-1591">
        <div className="flex items-center gap-4" data-id="element-1592">
          <div className="flex items-center gap-2" data-id="element-1593">
            <div className="w-4 h-4 rounded" style={{
            backgroundColor: COLORS.low
          }} data-id="element-1594"></div>
            <span data-id="element-1595">Low (&lt;15%)</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-1596">
            <div className="w-4 h-4 rounded" style={{
            backgroundColor: COLORS.medium
          }} data-id="element-1597"></div>
            <span data-id="element-1598">Medium (15-25%)</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-1599">
            <div className="w-4 h-4 rounded" style={{
            backgroundColor: COLORS.high
          }} data-id="element-1600"></div>
            <span data-id="element-1601">High (&gt;25%)</span>
          </div>
        </div>
        <div className="flex items-center gap-1" data-id="element-1602">
          <AlertTriangle size={14} className="text-amber-500" data-id="element-1603" />
          <span data-id="element-1604">Concentration alert</span>
        </div>
      </div>
    </div>;
};
export function ExposureAccumulation() {
  const breachCount = lineSizeData.filter(d => d.maximum > d.threshold).length;
  const highConcentration = occupancyExposureData.filter(d => d.percentage > 90).length;
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-1605">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-1606">
        <div data-id="element-1607">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1608">
            Exposure, Line Size & Accumulation
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-1609">
            Concentration risk and aggregation control • FY 2024
          </p>
        </div>
        <div className="flex items-center gap-4" data-id="element-1610">
          {breachCount > 0 && <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 rounded-full border border-amber-200" data-id="element-1611">
              <AlertTriangle className="text-amber-600" size={16} data-id="element-1612" />
              <span className="text-sm font-medium text-amber-700" data-id="element-1613">
                {breachCount} Threshold Breaches
              </span>
            </div>}
          <button className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-1614">
            <Download size={16} className="mr-2" data-id="element-1615" />
            Export Report
          </button>
        </div>
      </div>

      {/* Top Row: Line Size Trend */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1616">
        <div className="flex justify-between items-center mb-6" data-id="element-1617">
          <div data-id="element-1618">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1619">
              Line Size Trend
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1620">
              Average vs Maximum exposure per policy
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm" data-id="element-1621">
            <div className="flex items-center gap-2" data-id="element-1622">
              <div className="w-3 h-0.5 bg-blue-600" data-id="element-1623"></div>
              <span className="text-gray-600" data-id="element-1624">Average</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1625">
              <div className="w-3 h-0.5 bg-red-600" data-id="element-1626"></div>
              <span className="text-gray-600" data-id="element-1627">Maximum</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1628">
              <div className="w-3 h-0.5 bg-amber-500 border-t border-dashed" data-id="element-1629"></div>
              <span className="text-gray-600" data-id="element-1630">Threshold (£3M)</span>
            </div>
          </div>
        </div>
        <div className="h-80" data-id="element-1631">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1632">
            <LineChart data={lineSizeData} margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-1633">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-1634" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 12
            }} dy={10} data-id="element-1635" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 12
            }} tickFormatter={value => `£${value}K`} data-id="element-1636" />
              <Tooltip content={<CustomTooltip unit="£K" data-id="element-1638" />} data-id="element-1637" />
              <ReferenceLine y={3000} stroke={COLORS.threshold} strokeDasharray="5 5" strokeWidth={2} label={{
              value: 'Threshold',
              position: 'right',
              fill: '#F59E0B',
              fontSize: 12
            }} data-id="element-1639" />
              <Line type="monotone" dataKey="average" name="Average Line Size" stroke={COLORS.average} strokeWidth={2} dot={{
              r: 3
            }} activeDot={{
              r: 6
            }} data-id="element-1640" />
              <Line type="monotone" dataKey="maximum" name="Maximum Line Size" stroke={COLORS.maximum} strokeWidth={2} dot={{
              r: 3
            }} activeDot={{
              r: 6
            }} data-id="element-1641" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Middle Row: Two Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1642">
        {/* Policy Limits Distribution */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1643">
          <h2 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-1644">
            Policy Limits Distribution
          </h2>
          <div className="h-64" data-id="element-1645">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1646">
              <BarChart data={policyLimitsData} margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-1647">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-1648" />
                <XAxis dataKey="range" axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 11
              }} dy={10} angle={-15} textAnchor="end" data-id="element-1649" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} label={{
                value: 'Policy Count',
                angle: -90,
                position: 'insideLeft',
                fill: '#6B7280'
              }} data-id="element-1650" />
                <Tooltip content={<CustomTooltip unit="" data-id="element-1652" />} data-id="element-1651" />
                <Bar dataKey="count" name="Policies" radius={[4, 4, 0, 0]} data-id="element-1653">
                  {policyLimitsData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS.average} opacity={0.4 + index * 0.1} data-id="element-1654" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-4 text-sm text-gray-500" data-id="element-1655">
            <span className="font-semibold text-gray-900" data-id="element-1656">64%</span> of policies
            have limits under £500K
          </p>
        </div>

        {/* Geographic Exposure Heatmap */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1657">
          <div className="flex items-center justify-between mb-6" data-id="element-1658">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1659">
              Geographic Exposure
            </h2>
            <MapPin className="text-gray-400" size={20} data-id="element-1660" />
          </div>
          <GeographicHeatmap data-id="element-1661" />
        </div>
      </div>

      {/* Bottom Row: Occupancy Exposure */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1662">
        <div className="flex justify-between items-center mb-6" data-id="element-1663">
          <div data-id="element-1664">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1665">
              Exposure by Occupancy
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1666">
              Current exposure vs concentration limits
            </p>
          </div>
          {highConcentration > 0 && <div className="flex items-center gap-2 px-3 py-1 bg-red-50 rounded-full border border-red-200" data-id="element-1667">
              <AlertTriangle className="text-red-600" size={16} data-id="element-1668" />
              <span className="text-sm font-medium text-red-700" data-id="element-1669">
                {highConcentration} Near Limit
              </span>
            </div>}
        </div>
        <div className="h-80" data-id="element-1670">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1671">
            <BarChart data={occupancyExposureData} margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 80
          }} layout="vertical" data-id="element-1672">
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" data-id="element-1673" />
              <XAxis type="number" axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 12
            }} tickFormatter={value => `£${value}K`} data-id="element-1674" />
              <YAxis dataKey="occupancy" type="category" axisLine={false} tickLine={false} tick={{
              fill: '#4B5563',
              fontSize: 12
            }} width={120} data-id="element-1675" />
              <Tooltip content={<CustomTooltip unit="£K" data-id="element-1677" />} data-id="element-1676" />
              <Bar dataKey="exposure" name="Current Exposure" radius={[0, 4, 4, 0]} data-id="element-1678">
                {occupancyExposureData.map((entry, index) => <Cell key={`cell-${index}`} fill={getIntensityColor(entry.percentage)} data-id="element-1679" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 flex items-center justify-between text-xs text-gray-500" data-id="element-1680">
          <div className="flex items-center gap-4" data-id="element-1681">
            <div className="flex items-center gap-2" data-id="element-1682">
              <div className="w-4 h-4 rounded" style={{
              backgroundColor: COLORS.low
            }} data-id="element-1683"></div>
              <span data-id="element-1684">&lt;50% of limit</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1685">
              <div className="w-4 h-4 rounded" style={{
              backgroundColor: COLORS.medium
            }} data-id="element-1686"></div>
              <span data-id="element-1687">50-75%</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1688">
              <div className="w-4 h-4 rounded" style={{
              backgroundColor: COLORS.high
            }} data-id="element-1689"></div>
              <span data-id="element-1690">75-90%</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1691">
              <div className="w-4 h-4 rounded" style={{
              backgroundColor: COLORS.breach
            }} data-id="element-1692"></div>
              <span data-id="element-1693">&gt;90% (Near Limit)</span>
            </div>
          </div>
          <p className="italic" data-id="element-1694">
            Office Buildings approaching concentration limit (95%)
          </p>
        </div>
      </div>
    </div>;
}